import { CommonModule } from '@angular/common';
import { AfterContentInit, ChangeDetectionStrategy, Component, computed, ContentChildren, effect, ElementRef, EventEmitter, forwardRef, HostListener, inject, input, Input, OnDestroy, Output, QueryList, signal, TemplateRef, viewChild, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { InputTextModule } from '@gridscale/primeng17/inputtext';
import { SharedModule } from '../../helper/shared.module';
import { PrimeTemplate, SharedModule as PSharedModule } from '@gridscale/primeng17/api';
import { PrimeNGConfig } from '@gridscale/primeng17/api';
import * as _ from 'lodash';
import { AutocompleteOverlayComponent } from './autocomplete-overlay/autocomplete-overlay.component';
import { A11yLinkDirective } from '../../helper/directives/a11ylink.directive';
import { AutocompleteOverlayFocusDirective } from './autocomplete-overlay/autocomplete-overlay-focus.directive';
import { Nullable } from '@gridscale/primeng17/ts-helpers';
import { LoaderModule } from '../loader';


export interface AutocompleteSuggestion {
  label: string;
  value: unknown;
  [x: string]: unknown;
  children?: AutocompleteSuggestion[];
}

@Component({
    selector: 'ig-autocomplete',
    templateUrl: './autocomplete.component.html',
    styleUrls: ['./autocomplete.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        A11yLinkDirective,
        AutocompleteOverlayComponent,
        AutocompleteOverlayFocusDirective,
        CommonModule,
        FormsModule,
        InputTextModule,
        LoaderModule,
        PSharedModule,
        SharedModule,
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AutocompleteComponent),
            multi: true
        }
    ]
})
export class AutocompleteComponent implements ControlValueAccessor, AfterContentInit, OnDestroy {
  /**
   * When present, autocomplete clears the manual input if it does not match of the suggestions to force only accepting values from the suggestions.
   */
  @Input() forceSelection = false;

  /**
   * optional icon for the input field
   */
  @Input() icon?: string;

  /**
   * optional css class for the overlay panel
   */
  @Input() panelStyleClass?: string;

  /**
   * optional placeholder for input field
   */
  @Input() placeholder?: string = '';

  /**
   * optional id for the input field
   */
  @Input() inputId?: string;

  /**
   * input size
   */
  @Input() inputLarge = false;

  /**
   * if multiple values can be selected
   */
  @Input() multiple = false;

  /**
   * provide additonal fields, available in `suggestions`
   */
  @Input() additionalSearchFields: string[] = [];

  @Input() ariaLabelledby?: string;
  @Input() ariaLabel?: string;

  /**
   * emitted when the filter query changes
   */
  @Output() completeMethod = new EventEmitter<{ query: string }>();

  /**
   * emitted when a value was selected with the select value
   */
  @Output() onSelect = new EventEmitter<any>();

  /**
   * emitted when input is blurred
   */
  @Output() onBlur = new EventEmitter<any>();

  config = inject(PrimeNGConfig);

  disabled = signal(false);
  inputHasFocus = signal(false);
  inputValue = signal('');
  selectedValues = signal<AutocompleteSuggestion[] | undefined>(undefined);

  /**
   * the suggestions
   */
  suggestions = input.required<AutocompleteSuggestion[]>();
  suggestionsFilterMode = signal<'all' | 'filtered'>('all');
  suggestionsLoading = input<boolean>(false);
  suggestionsVisible = computed(() => {
    const inputValue = this.inputValue()?.toLowerCase().trim() || '';
    const suggestionsAll = this.suggestions();

    if (this.suggestionsFilterMode() === 'filtered' && inputValue !== '') {
      const filter = (suggestions: AutocompleteSuggestion[]): AutocompleteSuggestion[] => {
        if (inputValue.trim() === '') {
          return suggestions;
        }

        return suggestions
          .filter(s => {
            if (s.label.toLowerCase().indexOf(inputValue) >= 0) {
              return true;
            }
            let found = false;
            this.additionalSearchFields.forEach(searchField => {
              if ((_.get(s, [searchField], '') as string).toLowerCase().indexOf(inputValue) >= 0) {
                found = true;
              }
            })
            if (found) {
              return true;
            }
            return (s.children?.length && filter(s.children).length);
          })
          .map(s => s.children?.length ? { ...s, children: filter(s.children) } : { ...s });
      }

      return filter(suggestionsAll);
    }
    return suggestionsAll;
  });

  suggestionsAllFlat = computed(() => {
    const flatMapDeep = (arr: AutocompleteSuggestion[]): AutocompleteSuggestion[] => {
      return arr.flatMap(s => [...(s.children?.length ? flatMapDeep(s.children) : [s])]);
    }
    return flatMapDeep(this.suggestions());
  });

  suggestionsVisibleFlat = computed(() => {
    const flatMapDeep = (arr: AutocompleteSuggestion[]): AutocompleteSuggestion[] => {
      return arr.flatMap(s => [...(s.children?.length ? flatMapDeep(s.children) : [s])]);
    }
    return flatMapDeep(this.suggestionsVisible());
  });

  #value = signal<any | any[]>(null);

  templateItem: Nullable<TemplateRef<any>>;
  templateSelectedItem = signal<Nullable<TemplateRef<any>>>(undefined);
  templateEmpty: Nullable<TemplateRef<any>>;
  templateHeader: Nullable<TemplateRef<any>>;
  templateFooter: Nullable<TemplateRef<any>>;
  templateGroup: Nullable<TemplateRef<any>>;

  showCustomSelectedItemTemplate = computed(() => {
    const templateSelectedItem = this.templateSelectedItem();
    const inputHasFocus = this.inputHasFocus();
    const inputValue = this.inputValue();

    if (templateSelectedItem && !inputHasFocus && inputValue !== '') {
      return true;
    }
    return false;
  })


  myValue = computed(() => {
    const inputValue = this.inputValue();
    const selectedValues = this.selectedValues();

    if (this.multiple) {
      return selectedValues?.map(v => v.value);
    }
    if (!selectedValues?.length && !this.forceSelection) {
      return inputValue
    }
    return selectedValues?.at(0)?.value;
  })
  /**
   * @deprecated do not use here
   */
  value: any; // filled via the value accessor but not used here

  #lastInputValue?: string;
  #onChange = (...args: any) => { };
  #onTouched = () => { };

  focusIndex = signal<number>(-1);

  #lastValue: unknown = undefined;


  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (event.code === 'Escape') {
      this.onKeyEsc(event);
    }

    if (event.code === 'ArrowDown') {
      this.onKeyArrowDown(event);
    }

    if (event.code === 'ArrowUp') {
      this.onKeyArrowUp(event);
    }
  }

  footer = viewChild<ElementRef<HTMLElement>>('footer');

  constructor() {
    effect(() => {
      if (this.myValue() !== this.#lastValue) {
        this.#lastValue = this.myValue();
        this.#onChange(this.myValue());
        this.value = this.myValue();
        this.#value.set(this.myValue());
        this.#onTouched();
        window.setTimeout(() => {
          this.onSelect.emit(this.myValue());
        }, 100);
      }
    }, { allowSignalWrites: true });

    let lastSuggestions = '';
    effect(() => {

      const compareSuggestions = this.suggestions().map(s => s.value).sort().join(',');
      if (lastSuggestions !== compareSuggestions) {
        lastSuggestions = compareSuggestions;
        this.#onEvent({ preselectByValue: true });
      }

    }, { allowSignalWrites: true })
  }


  @ContentChildren(PrimeTemplate) templates: Nullable<QueryList<PrimeTemplate>>;

  @ViewChild('overlay', { static: true }) overlayEl?: AutocompleteOverlayComponent;
  @ViewChild('inputEl', { static: true }) inputEl?: ElementRef<HTMLInputElement>;

  ngOnDestroy(): void {
    if (this.overlayEl?.el.nativeElement) {
      (this.overlayEl?.el.nativeElement as HTMLElement).parentNode?.removeChild(this.overlayEl?.el.nativeElement);
    }
  }


  ngAfterContentInit() {
    (this.templates as QueryList<PrimeTemplate>).forEach((item) => {
      switch (item.getType()) {
        case 'item':
          this.templateItem = item.template;
          break;

        case 'selectedItem':
          this.templateSelectedItem.set(item.template);
          break;

        case 'group':
          this.templateGroup = item.template;
          break;

        case 'header':
          this.templateHeader = item.template;
          break;

        case 'empty':
          this.templateEmpty = item.template;
          break;

        case 'footer':
          this.templateFooter = item.template;
          break;

        default:
          this.templateItem = item.template;
          break;
      }
    });
  }

  /**
   * update the value from outsides form
   */
  writeValue(obj: any | any[]): void {
    this.#value.set(obj);
    this.#onEvent({ preselectByValue: true, resetInput: true })
    this.#lastValue = this.myValue();

  }
  /**
   * register an onchange handler from outside
   */
  registerOnChange(fn: () => void): void {
    this.#onChange = fn;
  }
  /**
   * register an ontouched handler from outside
   */
  registerOnTouched(fn: () => void): void {
    this.#onTouched = fn;
  }
  /**
   * set disabled state from outside
   */
  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  /**
   * handle keypress in the input field
   */
  #onKeyPressTimeout: number | undefined;
  onKeyPress(event: KeyboardEvent) {
    if (event.code === 'ArrowDown') {
      this.#onEvent({ show: true });
    }

    if (event.code === 'ArrowDown') {
      this.onKeyArrowDown(event);
      return;
    }


    if (event.code === 'Escape') {
      this.onKeyEsc(event);
      return;
    }

    if (this.#onKeyPressTimeout) {
      window.clearTimeout(this.#onKeyPressTimeout);
    }
    this.#onKeyPressTimeout = window.setTimeout(() => {
      if (this.inputValue() !== this.#lastInputValue) {
        this.suggestionsFilterMode.set('filtered');
        this.#onEvent({ show: true, emit: true });
      }

    }, 100)
  }



  onItemFocus(index: number) {
    this.focusIndex.set(index);
  }

  onItemBlur() {
    window.setTimeout(() => {
      if (!this.#focusInOverlay()) {
        this.focusIndex.set(-1);
        this.overlayEl?.hide();
      }
    }, 100)
  }

  #blurTimeout?: number;
  onInputFocus(event: FocusEvent) {
    if (this.#blurTimeout) {
      window.clearTimeout(this.#blurTimeout);
      this.#blurTimeout = undefined;
    }
    this.focusIndex.set(-1);
    this.#onEvent({ show: true, emit: true });

    this.inputHasFocus.set(true);
  }

  onInputBlur(event: FocusEvent) {
    // check if the input still matches selected value otherwise search for a selected value (or set undefined if not found)
    this.#blurTimeout = window.setTimeout(() => {
      if (!this.#focusInOverlay()) {
        this.#onEvent({ preselectByLabel: true, resetInput: true })

        // hide when the new focus is not in the overlay...
        this.overlayEl?.hide();
      }
    }, 100);

    this.inputHasFocus.set(false);
    this.onBlur.emit();
  }

  #focusInOverlay() {
    let focusInOverlay = false;
    if (this.overlayEl?.el.nativeElement) {
      let el = document.activeElement;
      while (el?.parentNode && !focusInOverlay) {
        if (el === this.overlayEl.el.nativeElement) {
          focusInOverlay = true;
        }

        el = el.parentNode as Element;
      }
    }

    return focusInOverlay;
  }

  #show() {
    if (!this.overlayEl?.visible()) {
      this.suggestionsFilterMode.set('all');
      this.overlayEl?.show();
  // this.focusFirst();
    }
  }


  onRemoveSelected(item: AutocompleteSuggestion) {
    const newValues = (this.selectedValues() || []).filter(v => !_.isEqual(v.value, item.value));
    this.selectedValues.set([...newValues]);
  }

  getFlatIndex(value: any) {
    return this.suggestionsVisibleFlat().findIndex(v => _.isEqual(v, value));
  }

  isSelected(value: any) {
    return !!this.selectedValues()?.find(v => _.isEqual(v.value, value));
  }


  onSelectSuggestion(suggestion: AutocompleteSuggestion) {
    if (this.isSelected(suggestion.value)) {
      if (this.multiple) {
        // remove it
        this.selectedValues.set(this.selectedValues()?.filter(s => !_.isEqual(s, suggestion)));
      }

    } else {
      // add it
      if (this.multiple) {
        this.selectedValues.set([...this.selectedValues() || [], suggestion]);
      } else {
        this.selectedValues.set([suggestion]);
      }
    }

    if (this.multiple) {
      this.inputValue.set('');

    } else if (this.selectedValues()?.length) {
      this.inputValue.set(this.selectedValues()![0].label);
    }


    this.inputEl?.nativeElement.focus();
    window.setTimeout(() => {
      this.overlayEl?.hide();
    }, 100);
  }

  focusFirst() {
    if (this.selectedValues()?.length) {
      const index = this.suggestionsVisibleFlat().map(s => s.value).findIndex(value => this.isSelected(value));

      if (index >= 0 && index < (this.suggestionsVisibleFlat().length)) {
        this.focusIndex.set(index);
      } else if (this.suggestionsVisibleFlat().length) {
        this.focusIndex.set(0);
      }

    } else if (this.suggestionsVisibleFlat().length) {
      this.focusIndex.set(0);
    }
  }

  focusNext() {
    if (this.focusIndex() >= 0) {
      const index = this.focusIndex();
      if (index >= 0 && index < (this.suggestionsVisibleFlat().length - 1)) {
        this.focusIndex.set(index + 1);
      } else if (index === (this.suggestionsVisibleFlat().length - 1)) {
        // check if there is sth focusable in the footer
        console.log('footer', this.footer()?.nativeElement)
        const focusable: HTMLElement | undefined | null = this.footer()?.nativeElement?.querySelector('[tabindex="0"]');
        if (focusable) {
          console.log({ focusable })
          focusable.focus();
          this.focusIndex.set(-1);
        }
      }
    } else {
      this.focusFirst();
    }
  }

  focusPrev() {
    if (this.focusIndex() >= 0) {
      const index = this.focusIndex();
      if (index > 0) {
        this.focusIndex.set(index - 1);
      } else {
        this.focusIndex.set(-1);
        this.inputEl?.nativeElement.focus()
      }
    }
  }

  /**
   * triggered from local host listener or from the overlay component
   */
  onKeyEsc(event: KeyboardEvent) {
    if (this.overlayEl?.visible()) {
      event.preventDefault();
      this.overlayEl?.hide();
      this.inputEl?.nativeElement.focus()
    }
  }
  /**
   * triggered from local host listener or from the overlay component
   */
  onKeyArrowDown(event: KeyboardEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.focusNext();
  }
  /**
   * triggered from local host listener or from the overlay component
   */
  onKeyArrowUp(event: KeyboardEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.focusPrev();
  }

  /**
   * Called when new suggestions came in or a new value is written from outside or value is changed from inside
   */
  #onEvent(actions: { preselectByValue?: boolean, preselectByLabel?: boolean; resetInput?: boolean, show?: boolean, emit?: boolean }) {
    if (actions.preselectByValue) {
      if (this.multiple && _.isArray(this.#value())) {
        if (actions.resetInput) {
          this.inputValue.set('');
        }

        const matchingSuggestions: AutocompleteSuggestion[] = [];
        this.#value().forEach((i: any) => {
          matchingSuggestions.push(...this.suggestionsAllFlat().filter(s => s.value !== undefined && _.isEqual(s.value, i)));
        })
        this.selectedValues.set([...matchingSuggestions]);

      } else {
        const matchingSuggestion = this.suggestionsAllFlat().find(s => s.value !== undefined && _.isEqual(s.value, this.#value()));

        if (matchingSuggestion) {
          this.inputValue.set(matchingSuggestion.label);
          this.selectedValues.set([matchingSuggestion]);

        } else if (this.forceSelection) {
          if (actions.resetInput) {
            this.inputValue.set('');
          }
          this.selectedValues.set([]);
        }
      }
    }

    if (actions.preselectByLabel && !this.multiple) {
      const matchingSuggestion = this.suggestionsAllFlat().find(s => s.label !== undefined && _.isEqual(s.label.toLowerCase(), this.inputValue().toLowerCase()));

      if (matchingSuggestion) {
        this.inputValue.set(matchingSuggestion.label);
        this.selectedValues.set([matchingSuggestion]);

      } else {
        if (this.forceSelection && actions.resetInput) {
          this.inputValue.set('');
        }
        this.selectedValues.set([]);
      }
    }

    if (actions.show) {
      this.#show();
    }

    if (actions.emit) {
      this.completeMethod.emit({ query: this.suggestionsFilterMode() === 'all' ? '' : this.inputValue() });
    }
  }

  get debugValue() {
    return this.#value;
  }
}
